import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import CallMadeOutlined from "@mui/icons-material/CallMadeOutlined";
import CallReceivedOutlined from "@mui/icons-material/CallReceivedOutlined";
import BarChartOutlined from "@mui/icons-material/BarChartOutlined";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import RequestQuoteOutlined from "@mui/icons-material/RequestQuoteOutlined";
import EventOutlined from "@mui/icons-material/EventOutlined";
import ShoppingCartOutlined from "@mui/icons-material/ShoppingCartOutlined";
import TrendingUpOutlined from "@mui/icons-material/TrendingUpOutlined";
import UndoOutlined from "@mui/icons-material/UndoOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import type {
  DashboardFinanceiroFiltros,
  DashboardFinanceiroViewModel,
  TipoDataInscricoesDashboard,
} from "../types/financeiro-dashboard.types";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";
import FinanceiroResumoCard from "./FinanceiroResumoCard";
import FinanceiroSection from "./FinanceiroSection";
import FinanceiroGlossario from "./FinanceiroGlossario";
import EfiAccountBalanceCard from "components/EfiAccountBalanceCard";
import {
  buildLancamentosVendasPath,
  type FinanceiroDashboardPeriodo,
} from "../../financeiro-periodo-storage";
import MDButton from "components/MDButton";

const sizeIcon = { fontSize: 20 } as const;

const ROTA_CONTAS_RECEBER = "/loja/financeiro/contas-receber";
const ROTA_DRE = "/loja/financeiro/dre-gerencial";
const ROTA_FINANCEIRO_EVENTOS = "/financeiro/financeiro-eventos";
const ROTA_EXTRATO_INSCRICOES = "/financeiro/extrato";

const SUBTITULO_INSCRICOES: Record<TipoDataInscricoesDashboard, string> = {
  pagamento:
    "Por data de pagamento → Confirmadas. Não inclui gratuitas, pendentes ou canceladas.",
  inscricao:
    "Por data de inscrição → Confirmadas. Não inclui gratuitas, pendentes ou canceladas.",
};

type Props = {
  vm: DashboardFinanceiroViewModel | null;
  loading: boolean;
  filtrosAplicados?: DashboardFinanceiroFiltros;
  onTipoDataInscricoesChange?: (tipo: TipoDataInscricoesDashboard) => void;
};

function periodoFromFiltros(
  filtros?: DashboardFinanceiroFiltros
): FinanceiroDashboardPeriodo | null {
  if (!filtros?.dataInicio?.trim() || !filtros?.dataFim?.trim()) return null;
  return {
    dataInicio: filtros.dataInicio.trim(),
    dataFim: filtros.dataFim.trim(),
  };
}

export default function FinanceiroResumoCards({
  vm,
  loading,
  filtrosAplicados,
  onTipoDataInscricoesChange,
}: Props) {
  const d = vm;
  const periodo = periodoFromFiltros(filtrosAplicados);
  const rotaLancamentos = buildLancamentosVendasPath(periodo);
  const tipoDataInscricoes =
    filtrosAplicados?.tipoDataInscricoes ?? "pagamento";

  const juros = d?.vendas.jurosParcelamentoTotal ?? 0;
  const jurosInscricoes = d?.inscricoes.jurosParcelamentoTotal ?? 0;
  const estornos = d?.vendas.estornosTotal ?? 0;
  const faturamentoLiquido = d?.vendas.faturamentoLiquido ?? 0;
  const descricaoFaturamento =
    juros >= 0.01
      ? `Valor cobrado no período (inclui ${formatarMoedaBRL(juros)} de juros de parcelamento)`
      : "Valor cobrado confirmado no período (pode incluir juros de cartão)";

  const descricaoFaturamentoInscricoes =
    jurosInscricoes >= 0.01
      ? `Valor recebido no período (inclui ${formatarMoedaBRL(jurosInscricoes)} de juros de parcelamento)`
      : "Valor recebido confirmado no período (pode incluir juros de cartão)";

  return (
    <>
      <EfiAccountBalanceCard sx={{ mb: 2 }} />

      <FinanceiroSection
        titulo="Movimentações do dia"
        subtitulo="Entradas e saídas registradas hoje (calendário de São Paulo)"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FinanceiroResumoCard
              titulo="Entradas hoje"
              valor={d?.caixa.entradasHoje}
              formato="moeda"
              loading={loading}
              tipo="positivo"
              icon={<CallReceivedOutlined sx={sizeIcon} />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FinanceiroResumoCard
              titulo="Saídas hoje"
              valor={d?.caixa.saidasHoje}
              formato="moeda"
              loading={loading}
              icon={<CallMadeOutlined sx={sizeIcon} />}
            />
          </Grid>
        </Grid>
      </FinanceiroSection>

      <FinanceiroSection
        titulo="Financeiro"
        subtitulo="Posição de títulos (contas a receber / pagar)"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Contas a receber (aberto)"
              valor={d?.financeiro.contasReceber}
              formato="moeda"
              loading={loading}
              icon={<RequestQuoteOutlined sx={sizeIcon} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Contas a pagar (aberto)"
              valor={d?.financeiro.contasPagar}
              formato="moeda"
              loading={loading}
              icon={<PaymentsOutlined sx={sizeIcon} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Vencidos (CR + CP)"
              valor={d?.financeiro.vencidosTotal}
              formato="moeda"
              loading={loading}
              tipo="alerta"
              descricao="Soma de títulos vencidos ou em atraso"
              icon={<WarningAmberOutlined sx={sizeIcon} />}
              to={ROTA_CONTAS_RECEBER}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Previsão 30 dias — a receber"
              valor={d?.financeiro.previsaoReceber30Dias}
              formato="moeda"
              loading={loading}
              descricao={`Pagar no período: ${d ? formatarMoedaBRL(d.financeiro.previsaoPagar30Dias) : "—"}`}
              icon={<CalendarMonthOutlined sx={sizeIcon} />}
            />
          </Grid>
        </Grid>
      </FinanceiroSection>

      <FinanceiroSection
        titulo="Vendas de inscrições"
        subtitulo={SUBTITULO_INSCRICOES[tipoDataInscricoes]}
        acao={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <MDButton
              component={Link}
              to={ROTA_EXTRATO_INSCRICOES}
              size="small"
              variant="outlined"
              color="primary"
            >
              Extrato
            </MDButton>
            <MDButton
              component={Link}
              to={ROTA_FINANCEIRO_EVENTOS}
              size="small"
              variant="contained"
              color="primary"
            >
              Por evento
            </MDButton>
          </Stack>
        }
      >
        <FormControl sx={{ mb: 2 }}>
          <FormLabel id="dashboard-tipo-data-inscricoes">Filtrar por</FormLabel>
          <RadioGroup
            row
            aria-labelledby="dashboard-tipo-data-inscricoes"
            name="dashboard-tipo-data-inscricoes"
            value={tipoDataInscricoes}
            onChange={(e) =>
              onTipoDataInscricoesChange?.(
                e.target.value as TipoDataInscricoesDashboard
              )
            }
          >
            <FormControlLabel
              value="pagamento"
              control={<Radio size="small" />}
              label="Data de pagamento"
            />
            <FormControlLabel
              value="inscricao"
              control={<Radio size="small" />}
              label="Data de inscrição"
            />
          </RadioGroup>
          <Typography variant="caption" color="text.secondary">
            Compare &quot;Receita de inscrições&quot; e &quot;Inscrições com
            receita&quot; com Confirmadas no extrato (mesmo filtro de data).
          </Typography>
        </FormControl>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Faturamento bruto"
              valor={d?.inscricoes.faturamentoBruto}
              formato="moeda"
              loading={loading}
              descricao={descricaoFaturamentoInscricoes}
              icon={<BarChartOutlined sx={sizeIcon} />}
              to={ROTA_FINANCEIRO_EVENTOS}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Receita de inscrições"
              valor={d?.inscricoes.receitaInscricoes}
              formato="moeda"
              loading={loading}
              descricao="Total comercial das inscrições (sem juros de parcelamento)"
              icon={<EventOutlined sx={sizeIcon} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Juros de parcelamento"
              valor={jurosInscricoes >= 0.01 ? jurosInscricoes : 0}
              formato="moeda"
              loading={loading}
              indisponivel={!loading && jurosInscricoes < 0.01}
              descricao={
                jurosInscricoes >= 0.01
                  ? "Diferença entre valor recebido e total comercial das inscrições"
                  : "Sem juros de cartão no período"
              }
              icon={<TrendingUpOutlined sx={sizeIcon} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Ticket médio"
              valor={d?.inscricoes.ticketMedio}
              formato="moeda"
              loading={loading}
              descricao="Faturamento bruto ÷ inscrições com receita"
              icon={<TrendingUpOutlined sx={sizeIcon} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Inscrições com receita"
              valor={d?.inscricoes.quantidadeInscricoes}
              formato="numero"
              loading={loading}
              descricao="Inscrições pagas no período (status Pago)"
              icon={<ReceiptLongOutlined sx={sizeIcon} />}
              to={ROTA_FINANCEIRO_EVENTOS}
            />
          </Grid>
        </Grid>
      </FinanceiroSection>

      <FinanceiroSection
        titulo="Vendas da loja"
        subtitulo="Receitas e estornos de pedidos da loja virtual no período (data efetiva). Inscrições em eventos aparecem na seção abaixo."
        acao={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <MDButton
              component={Link}
              to={rotaLancamentos}
              size="small"
              variant="contained"
              color="primary"
            >
              Lançamentos
            </MDButton>
            <MDButton
              component={Link}
              to={ROTA_DRE}
              size="small"
              variant="contained"
              color="primary"
            >
              DRE Gerencial
            </MDButton>
          </Stack>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Faturamento bruto"
              valor={d?.vendas.faturamentoBruto}
              formato="moeda"
              loading={loading}
              descricao={descricaoFaturamento}
              icon={<BarChartOutlined sx={sizeIcon} />}
              to={rotaLancamentos}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Estornos"
              valor={estornos >= 0.01 ? estornos : 0}
              formato="moeda"
              loading={loading}
              tipo="negativo"
              indisponivel={!loading && estornos < 0.01}
              descricao={
                estornos >= 0.01
                  ? "Devoluções de pagamento de pedidos no período"
                  : "Sem estornos de pedidos no período"
              }
              icon={<UndoOutlined sx={sizeIcon} />}
              to={rotaLancamentos}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Faturamento líquido"
              valor={faturamentoLiquido}
              formato="moeda"
              loading={loading}
              tipo={
                faturamentoLiquido < 0
                  ? "negativo"
                  : faturamentoLiquido > 0
                    ? "positivo"
                    : "neutro"
              }
              descricao="Faturamento bruto − estornos (não inclui taxas de gateway)"
              icon={<PaymentsOutlined sx={sizeIcon} />}
              to={rotaLancamentos}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Receita comercial"
              valor={d?.vendas.receitaPedidos}
              formato="moeda"
              loading={loading}
              descricao="Total dos pedidos (sem juros de parcelamento)"
              icon={<ShoppingCartOutlined sx={sizeIcon} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Juros de parcelamento"
              valor={juros >= 0.01 ? juros : 0}
              formato="moeda"
              loading={loading}
              indisponivel={!loading && juros < 0.01}
              descricao={
                juros >= 0.01
                  ? "Diferença entre valor cobrado e total comercial dos pedidos"
                  : "Sem juros de cartão no período"
              }
              icon={<TrendingUpOutlined sx={sizeIcon} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Ticket médio"
              valor={d?.vendas.ticketMedio}
              formato="moeda"
              loading={loading}
              descricao="Faturamento bruto ÷ pedidos com receita"
              icon={<TrendingUpOutlined sx={sizeIcon} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FinanceiroResumoCard
              titulo="Pedidos com receita"
              valor={d?.vendas.quantidadePedidos}
              formato="numero"
              loading={loading}
              descricao="Pedidos distintos com receita de venda no período"
              icon={<ReceiptLongOutlined sx={sizeIcon} />}
              to={rotaLancamentos}
            />
          </Grid>
        </Grid>
      </FinanceiroSection>

      <FinanceiroGlossario />
    </>
  );
}
